// Import package components
import { createContext, useState } from "react";
import UIkit from "uikit";

import HolidaysService from "../services/holidays.service";

export const HolidaysContext = createContext();

export const HolidaysProvider = ({ children }) => {
  const [flagIsEditing, setIsEditing] = useState(false);
  const [flagIsNew, setIsNew] = useState(false);
  const [flagIsProcessing, setIsProcessing] = useState(false);

  const [listHolidays, setHolidays] = useState([]);
  const [objHoliday, setObjHoliday] = useState({});

  // optional: if you want pagination server-side
  const [totalCnt, setTotalCnt] = useState(0);

  const contextHolidays = {
    flagIsEditing,
    setIsEditing,
    flagIsNew,
    setIsNew,
    flagIsProcessing,

    listHolidays,
    setHolidays,

    objHoliday,
    setObjHoliday,

    totalCnt,

    // LIST / SEARCH
    searchHolidays: async (criteria) => {
      setIsProcessing(true);
      try {
        const res = await HolidaysService.searchHolidays(criteria);
        // expected: { rows: [], total: 123 } or just []
        const data = res?.data;

        if (Array.isArray(data)) {
          setHolidays(data);
          setTotalCnt(data.length);
        } else {
          setHolidays(data?.rows || []);
          setTotalCnt(Number(data?.total || 0));
        }

        setIsProcessing(false);
        return true;
      } catch (err) {
        console.error(err);
        setIsProcessing(false);
        UIkit.notification("Failed to load holidays", "error");
        return false;
      }
    },

    // CREATE
    insertHoliday: async (holiday) => {
      setIsProcessing(true);
      try {
        const res = await HolidaysService.insertHoliday(holiday);
        setIsProcessing(false);

        // optional: optimistic update
        if (res?.data) {
          setHolidays((prev) => [res.data, ...prev]);
          UIkit.notification("Holiday added", "success");
        }
        return res?.data;
      } catch (error) {
        console.error(error);
        setIsProcessing(false);
        UIkit.notification("Failed to add holiday", "error");
        return null;
      }
    },

    // UPDATE
    updateHoliday: async (holiday) => {
      setIsProcessing(true);
      try {
        const res = await HolidaysService.updateHoliday(holiday);
        setIsProcessing(false);

        // optional: update list
        if (res?.data) {
          setHolidays((prev) =>
            prev.map((h) => (h.id === res.data.id ? res.data : h)),
          );
          UIkit.notification("Holiday updated", "success");
        }
        return res?.data;
      } catch (error) {
        console.error(error);
        setIsProcessing(false);
        UIkit.notification("Failed to update holiday", "error");
        return null;
      }
    },

    // DELETE
    deleteHoliday: async (holiday) => {
      setIsProcessing(true);
      try {
        const res = await HolidaysService.deleteHoliday(holiday);
        setIsProcessing(false);

        const deleted = res?.data;
        if (deleted?.id) {
          setHolidays((prev) => prev.filter((h) => h.id !== deleted.id));
          UIkit.notification("Holiday deleted", "success");
        }
        return deleted;
      } catch (error) {
        console.error(error);
        setIsProcessing(false);
        UIkit.notification("Failed to delete holiday", "error");
        return null;
      }
    },

    // OPTIONAL: bulk load national holidays for a year/country
    preloadNationalHolidays: async ({ year, country }) => {
      setIsProcessing(true);
      try {
        const res = await HolidaysService.preloadNationalHolidays({
          year,
          country,
        });
        setIsProcessing(false);

        // expect res.data = inserted holidays array (or summary)
        UIkit.notification("National holidays loaded", "success");

        // refresh list after load (recommended)
        return res?.data;
      } catch (error) {
        console.error(error);
        setIsProcessing(false);
        UIkit.notification("Failed to load national holidays", "error");
        return null;
      }
    },
  };

  return (
    <HolidaysContext.Provider value={contextHolidays}>
      {children}
    </HolidaysContext.Provider>
  );
};